import { useQuery, useMutation } from '@tanstack/react-query'
import { apiService } from './apiService'

/**
 * Fetch fee code data for catalog creation
 * @returns {Promise<Array>} - Array of fee codes with their details
 * @throws {Error} - If the API call fails
 */
export const FEE_CODES_CACHE_KEY = 'fee_codes_cache_v1'

const fetchFeeCodes = async () => {
    const readCache = () => {
        try {
            const raw = typeof window !== 'undefined' && window.localStorage.getItem(FEE_CODES_CACHE_KEY)
            if (!raw) return null
            const parsed = JSON.parse(raw)
            return Array.isArray(parsed) ? parsed : null
        } catch (e) {
            console.warn('Failed to read fee codes cache:', e)
            return null
        }
    }

    const writeCache = (codes) => {
        try {
            if (typeof window === 'undefined') return
            window.localStorage.setItem(FEE_CODES_CACHE_KEY, JSON.stringify(codes))
        } catch (e) {
            console.warn('Failed to write fee codes cache:', e)
        }
    }

    try {
        const response = await apiService.get('/fee_codes', {}, 'v1')
        const payload = Array.isArray(response) ? response : response?.data

        if (Array.isArray(payload) && payload.length > 0) {
            const mapped = payload.map((fee) => ({ id: fee.id, code: fee.code }))
            writeCache(mapped)
            return mapped
        }

        const cached = readCache()
        if (cached) return cached
        return []
    } catch (error) {
        console.error('Error fetching fee codes:', error)
        const cached = readCache()
        if (cached) return cached
        return []
    }
}

/**
 * Hook to fetch fee code data
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result containing fee codes
 */
export const useFeeCodes = (options = {}) => {
    return useQuery({
        queryKey: ['feeCodes'],
        queryFn: fetchFeeCodes,
        ...options,
    })
}

/**
 * Fetch catalog data by search text
 * @param {string} searchText - Text to search for
 * @param {number} page - Page number (1-indexed)
 * @param {number} perPage - Number of items per page
 * @returns {Promise<Object>} - Catalog data
 */
const fetchCatalogsBySearch = async (searchText, page = 1, perPage = 10) => {
    const endpoint = `/evaluations?allNames=${encodeURIComponent(searchText)}&page=${page}&per_page=${perPage}`
    return apiService.get(endpoint)
}

/**
 * Fetch catalog data by ID
 * @param {string} evalId - Evaluation ID
 * @returns {Promise<Object>} - Catalog data
 */
const fetchCatalogById = async (evalId) => {
    if (!evalId) return null
    const endpoint = `/evaluations/${evalId}`
    return apiService.get(endpoint, {}, 'v1')
}

/**
 * Fetch catalog data by URI
 * @param {string} uri - Evaluation URI
 * @returns {Promise<Object>} - Catalog data
 */
const fetchCatalogByUri = async (uri) => {
    if (!uri) return null
    return apiService.get(uri)
}

/**
 * Fetch asset data by ID
 * @param {string} itemId - Item ID
 * @returns {Promise<Object>} - Asset data
 */
const fetchAssetById = async (itemId) => {
    if (!itemId) return null
    const endpoint = `/items/${itemId}`
    return apiService.get(endpoint, {}, 'v1')
}

/**
 * Fetch all users
 * @returns {Promise<Object>} - Users data
 */
const fetchAllUsers = async () => {
    return apiService.get('/all_users', {}, 'v3')
}

/**
 * Hook to fetch catalog data by search text
 * @param {string} searchText - Text to search for
 * @param {number} page - Page number (1-indexed)
 * @param {number} perPage - Number of items per page
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useCatalogSearch = (
    searchText,
    page = 1,
    perPage = 10,
    options = {}
) => {
    return useQuery({
        queryKey: ['catalogs', 'search', searchText, page, perPage],
        queryFn: () => fetchCatalogsBySearch(searchText, page, perPage),
        enabled: !!searchText,
        ...options,
    })
}

/**
 * Hook to fetch catalog data by ID
 * @param {string} evalId - Evaluation ID
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useCatalogById = (evalId, options = {}) => {
    return useQuery({
        queryKey: ['catalogs', 'id', evalId],
        queryFn: () => fetchCatalogById(evalId),
        enabled: !!evalId,
        ...options,
    })
}

/**
 * Hook to fetch catalog data by URI
 * @param {string} uri - Evaluation URI
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useCatalogByUri = (uri, options = {}) => {
    return useQuery({
        queryKey: ['catalogs', 'uri', uri],
        queryFn: () => fetchCatalogByUri(uri),
        enabled: !!uri,
        ...options,
    })
}

/**
 * Hook to fetch asset data by ID
 * @param {string} itemId - Item ID
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useAssetById = (itemId, options = {}) => {
    return useQuery({
        queryKey: ['assets', 'id', itemId],
        queryFn: () => fetchAssetById(itemId),
        enabled: !!itemId,
        ...options,
    })
}

/**
 * Hook to fetch all users
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useAllUsers = (options = {}) => {
    return useQuery({
        queryKey: ['users', 'all'],
        queryFn: () => fetchAllUsers(),
        staleTime: 5 * 60 * 1000, // 5 minutes
        ...options,
    })
}

/**
 * Hook to fetch employees data
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useEmployees = (options = {}) => {
    return useQuery({
        queryKey: ['employees'],
        queryFn: async () => {
            const endpoint = '/employees'
            return apiService.get(endpoint)
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        ...options,
    })
}

/**
 * Hook to fetch packages data
 * @param {Object} params - Parameters for the query
 * @param {string[]} params.selectedEmployees - Selected employees
 * @param {string[]} params.filter - Status filters
 * @param {number} params.pageNumber - Page number (0-indexed)
 * @param {number} params.rowsPerPage - Number of rows per page
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const usePackages = (
    { selectedEmployees, filter, pageNumber, rowsPerPage },
    options = {}
) => {
    const employeesParam = selectedEmployees?.join(',') || ''
    const statusParam = filter?.join(',') || ''

    return useQuery({
        queryKey: [
            'packages',
            employeesParam,
            statusParam,
            pageNumber,
            rowsPerPage,
        ],
        queryFn: async () => {
            const endpoint = `/packages?page=${pageNumber + 1}&size=${rowsPerPage}&by_user=${employeesParam}&status=${statusParam}`
            return apiService.get(endpoint)
        },
        ...options,
    })
}
